/**
 * <fitment-table>
 *
 * Live-filters the DS fitment rows by year (dropdown, auto-populated from the data) and a
 * model search box, and shows a "fits your bike" verdict. Collapses to a few rows until the
 * shopper expands or filters. Pure client-side, no dependencies.
 */
class FitmentTable extends HTMLElement {
  connectedCallback() {
    this.search = this.querySelector('[data-fit-search]');
    this.yearSel = this.querySelector('[data-fit-year]');
    this.count = this.querySelector('[data-fit-count]');
    this.toggle = this.querySelector('[data-fit-toggle]');
    this.rows = Array.from(this.querySelectorAll('tbody tr'));
    if (!this.rows.length) return;

    this.collapsed = true;
    this.limit = parseInt(this.dataset.limit || '10', 10) || 10;

    const years = new Set();
    for (const r of this.rows) {
      r._years = this.#expand(r.dataset.years || '');
      r._years.forEach((y) => years.add(y));
    }
    if (this.yearSel) {
      [...years].sort((a, b) => b - a).forEach((y) => {
        const o = document.createElement('option');
        o.value = y;
        o.textContent = y;
        this.yearSel.appendChild(o);
      });
      this.yearSel.addEventListener('change', () => this.#apply());
    }
    this.search?.addEventListener('input', () => this.#apply());
    this.toggle?.addEventListener('click', () => {
      this.collapsed = !this.collapsed;
      this.#apply();
    });
    this.#apply();
  }

  #expand(str) {
    const set = new Set();
    for (const part of str.split(',')) {
      const p = part.trim();
      if (!p) continue;
      if (p.includes('-')) {
        const [a, b] = p.split('-').map(Number);
        for (let y = a; y <= b; y++) set.add(y);
      } else {
        set.add(Number(p));
      }
    }
    return set;
  }

  #apply() {
    const q = (this.search?.value || '').trim().toLowerCase();
    const y = this.yearSel && this.yearSel.value ? Number(this.yearSel.value) : null;
    const filtering = !!(q || y);

    const matches = [];
    for (const r of this.rows) {
      const okY = !y || r._years.has(y);
      const okQ = !q || (r.dataset.model || '').includes(q) || r.textContent.toLowerCase().includes(q);
      if (okY && okQ) matches.push(r);
      else r.hidden = true;
    }
    matches.forEach((r, i) => {
      r.hidden = !filtering && this.collapsed && i >= this.limit;
    });

    const total = this.rows.length;
    if (this.count) {
      if (y) {
        this.count.innerHTML = matches.length
          ? `<span class="fitment__yes">✓ Fits ${matches.length} model${matches.length > 1 ? 's' : ''} from ${y}</span>`
          : `<span class="fitment__no">No listed fitment for ${y}</span>`;
      } else {
        this.count.textContent = filtering ? `${matches.length} of ${total} models` : `${total} models listed`;
      }
    }
    if (this.toggle) {
      if (filtering) {
        this.toggle.hidden = true;
      } else {
        this.toggle.hidden = total <= this.limit;
        this.toggle.textContent = this.collapsed ? `Show all ${total}` : 'Show fewer';
      }
    }
  }
}

if (!customElements.get('fitment-table')) {
  customElements.define('fitment-table', FitmentTable);
}
